import React, { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import Button from '../assets/components/Button/Button';
import { useT } from '../i18n';

interface IProps {
  error?: Error;
}

/**
 * Custom error component for Okta's LoginCallback.
 *
 * Okta's default behavior renders the raw error as text and leaves the user
 * stuck on /login/callback (e.g. "TypeError: Cannot read properties of null
 * (reading '0')"). This happens when the callback tokens/transaction cannot be
 * parsed (cleared storage, duplicated navigation, expired state).
 *
 * This component auto-recovers: if the user already has a valid session it
 * redirects home; otherwise it clears the stale Okta transaction/token storage
 * and restarts the sign-in redirect. A manual fallback button is always shown.
 */
const LoginCallbackError = ({ error }: IProps) => {
  const { oktaAuth } = useOktaAuth();
  const [recovering, setRecovering] = useState(true);
  const t = useT();

  useEffect(() => {
    let cancelled = false;

    const recover = async () => {
      try {
        const authenticated = await oktaAuth.isAuthenticated();
        if (cancelled) return;

        if (authenticated) {
          window.location.replace('/');
          return;
        }

        // Clear any stale transaction/token state left from the failed callback
        // so the next redirect starts clean.
        try {
          oktaAuth.transactionManager?.clear();
        } catch {
          /* no-op */
        }
        try {
          oktaAuth.tokenManager?.clear();
        } catch {
          /* no-op */
        }

        await oktaAuth.signInWithRedirect({ originalUri: '/' });
      } catch {
        if (!cancelled) setRecovering(false);
      }
    };

    recover();

    return () => {
      cancelled = true;
    };
  }, [oktaAuth, error]);

  const goHome = () => window.location.replace('/');

  return (
    <div className="App">
      <header className="App-header" style={{ textAlign: 'center', padding: '24px' }}>
        <p>
          {recovering
            ? t('login.retrying')
            : t('login.failed')}
        </p>
        <Button value="Volver al inicio" clickHandler={goHome} />
      </header>
    </div>
  );
};

export default LoginCallbackError;
